import { ContentItemViewNodesType } from '../../../types'
import { DataNode } from '../../../types/types'

/**
 * Check if a node is a MOPT (Manufacturer-sourced Product Type) article
 *
 * A MOPT article must satisfy ALL three conditions:
 * 1. textType === 'Article'
 * 2. isCreatedFromPremiumPlus === true
 * 3. partType === 'Products'
 *
 * This is the canonical MOPT detection function used across the application.
 *
 * @param node - The node to check
 * @returns true if the node is a MOPT article, false otherwise
 *
 * @example
 * const node = { textType: 'Article', isCreatedFromPremiumPlus: true, partType: 'Products' }
 * isMOPTArticle(node) // returns true
 *
 * const nonMOPT = { textType: 'Article', isCreatedFromPremiumPlus: true, partType: 'Divisions' }
 * isMOPTArticle(nonMOPT) // returns false (not Products)
 *
 * const paragraph = { textType: 'Paragraph', isCreatedFromPremiumPlus: true, partType: 'Products' }
 * isMOPTArticle(paragraph) // returns false (not an Article)
 */
export const isMOPTArticle = (
    node: Partial<ContentItemViewNodesType> | undefined
): boolean => {
    return !!(
        node?.textType === 'Article' &&
        node?.isCreatedFromPremiumPlus &&
        node?.partType === 'Products'
    )
}

/**
 * Check if a DataNode is a MOPT article
 *
 * This is a convenience function that checks both the node type and its contentItemView.
 * Use this when working with DataNode objects from the tree structure.
 *
 * @param node - The DataNode to check
 * @returns true if the node is a MOPT article, false otherwise
 *
 * @example
 * const dataNode: DataNode = { type: 'Article', contentItemView: { ... } }
 * isDataNodeMOPTArticle(dataNode) // checks both type and contentItemView
 */
export const isDataNodeMOPTArticle = (node?: DataNode): boolean => {
    return !!(
        node &&
        node.type === 'Article' &&
        node.contentItemView &&
        isMOPTArticle(node.contentItemView)
    )
}

/**
 * Validation result for MOPT placement operations
 */
export interface MOPTPlacementValidationResult {
    /** Whether the placement is valid */
    isValid: boolean
    /** Reason for invalid placement */
    reason?: 'MOPT_ADJACENCY'
}

/**
 * Validates if placing a node would violate MOPT restrictions
 *
 * Business Rule: Non-MOPT articles cannot be placed immediately AFTER a MOPT article.
 * However, they CAN be placed BEFORE a MOPT article.
 *
 * @param dragNode - The node being moved/placed
 * @param reorderedSiblings - The sibling array after reordering
 * @param finalIndex - The final position of the drag node in the reordered array
 * @returns Validation result with isValid flag and optional reason
 *
 * @example
 * // Block: Non-MOPT placed after MOPT
 * const siblings = [moptArticle, nonMoptArticle]
 * validateMOPTPlacement(nonMoptArticle, siblings, 1)
 * // returns { isValid: false, reason: 'MOPT_ADJACENCY' }
 *
 * // Allow: Non-MOPT placed before MOPT
 * const siblings = [nonMoptArticle, moptArticle]
 * validateMOPTPlacement(nonMoptArticle, siblings, 0)
 * // returns { isValid: true }
 */
export const validateMOPTPlacement = (
    dragNode: Partial<ContentItemViewNodesType>,
    reorderedSiblings: Partial<ContentItemViewNodesType>[],
    finalIndex: number
): MOPTPlacementValidationResult => {
    // Only validate if the drag node is a non-MOPT article
    if (dragNode.textType !== 'Article' || isMOPTArticle(dragNode)) {
        return { isValid: true }
    }

    // Check if there's a valid final index
    if (finalIndex === -1 || finalIndex >= reorderedSiblings.length) {
        return { isValid: true }
    }

    // Simple rule: only block if previous sibling (node before) is MOPT
    // Allow if next sibling (node after) is MOPT - placing BEFORE a MOPT is OK
    if (finalIndex > 0) {
        const previousSibling = reorderedSiblings[finalIndex - 1]
        if (previousSibling && isMOPTArticle(previousSibling)) {
            return { isValid: false, reason: 'MOPT_ADJACENCY' }
        }
    }

    return { isValid: true }
}
